/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Apr 04 2024
 **/
import { useRef } from "react";
import { useChat } from "@/frameworks/core/API";
import { ModalID } from "@/frameworks/core/commons";
import { DocumentViewer } from "@/frameworks/core/components";
import { useConfirm, useFileManager } from "@/frameworks/core/hooks";
import { useModal } from "@/frameworks/designStuff/components";
import { Utility, downloadBlobData } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { FileAPI } from "@/frameworks/core/API";

type ChatFileFormat = (ChatServices.Format2.Elements.File | ChatServices.Format2.Elements.Image) & {
	fileBoxID: string;
};

type FileType = {
	id: string;
	name: string;
	url: string;
	type: "file" | "image";
};

export default function usePreviewChatFile() {
	const modal = useModal();
	const { downloadFiles } = useFileManager();
	const { deleteFile } = useChat();
	const { confirm } = useConfirm();
	const fileAPIRef = useRef(new FileAPI());
	const fileAPI = fileAPIRef.current;

	const openDocumentViewer = async (
		file: ChatServices.Format2.Elements.File | ChatServices.Format2.Elements.Image,
		chatData: {
			userID: string;
			messageID: string;
			roomID: string;
			files: WebServices.Chat.Messages.Data["content"][number]["files"];
		},
		date?: number,
		onRemoveFile?: () => void,
		isHideFileSize?: boolean
	) => {
		const processFiles = (filterTypes: string[]) => {
			return chatData.files
				?.map((file) => {
					const data = JSON.parse(file.data) as ChatFileFormat;
					return {
						id: data.id,
						name: data.title,
						url: data.url,
						type: data.type
					};
				})
				.filter((data) => filterTypes.includes(data.type))
				.sort((a, b) => (a.type === "image" ? 0 : 1) - (b.type === "image" ? 0 : 1));
		};

		const imageList = processFiles(["image"])?.map(({ url, id }) => ({
			url,
			id
		}));

		const files = processFiles(["image", "file"]) as FileType[];

		const startIndex = files?.findIndex((v) => v.id === file.id);

		const fileDisplaySizes = isHideFileSize
			? undefined
			: files &&
				((await Promise.all(
					files?.map(async (file) => {
						const { data } = await fileAPI.get_file_detail({ id: file.id });
						return data.fileDisplaySize;
					})
				)) as string[]);

		/**
		 * Handle download files when preview files and selected file downloaded.
		 * The function will receive the fileId as parameter and find the file existing and start downloading
		 * Update: 03/02/2025
		 * @Ticket: 234753
		 * @author: DQTuan
		 * @param fileId
		 */
		const handleDownloadFileByFileId = (fileId: string | undefined) => {
			if (!fileId) return;
			const file = files.find((fileElement) => fileElement.id === fileId);
			if (file) {
				const { name, id } = file;
				downloadFiles({ id }).then((response) => {
					downloadBlobData(response as Blob, name || "");
				});
			}
		};

		modal.show({
			id: ModalID.DOCUMENT_VIEWER,
			content: (
				<DocumentViewer
					createByID={chatData.userID || ""}
					fileID={files?.map((file) => file.id)}
					fileName={files?.map((file) => file.name)}
					mimeType={file.type === "file" ? file.mime : "image/*"}
					imageList={imageList?.map((v) => v.url)}
					startIndex={startIndex === -1 ? 0 : startIndex}
					date={date}
					fileDisplaySize={!isHideFileSize ? fileDisplaySizes : undefined}
					onClose={() => {
						modal.hide(ModalID.DOCUMENT_VIEWER);
					}}
					onRemove={() => {
						if (Utility.isMobile) return;
						confirm(_T("해당 파일을 삭제하시겠습니까?"), {
							onConfirm: async () => {
								const params: WebServices.Chat.DeleteFile.Request = {
									fileBoxId: file.id,
									roomMessageId: chatData.messageID
								};
								await deleteFile.execute(params);
								onRemoveFile?.();
								modal.hide(ModalID.DOCUMENT_VIEWER);
							}
						});
					}}
					onDownload={handleDownloadFileByFileId}
				/>
			)
		});
	};

	const previewChatFile = ({
		chatContent,
		date,
		isHideFileSize,
		onRemoveFile,
		...chatData
	}: {
		files: WebServices.Chat.Messages.Data["content"][number]["files"];
		chatContent: ChatServices.Format2.Elements.AllRichtextElements;
		userID: string;
		messageID: string;
		roomID: string;
		date?: number;
		isHideFileSize?: boolean;
		onRemoveFile?: () => void;
	}) => {
		if (chatContent.type === "file" || chatContent.type === "image") {
			openDocumentViewer(chatContent, chatData, date, onRemoveFile, isHideFileSize);
		}
	};

	return { previewChatFile };
}
